import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * Lead-magnet email capture for the Home hero.
 * Sends { first_name, email } -> POST /api/leads/subscribe
 * On success shows "Check your inbox" + a direct PDF download link.
 * Styled to sit on top of the dark hero (glass, blush-pink accents).
 */
export default function LeadCapture() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done
  const [downloadUrl, setDownloadUrl] = useState("/airbnb-launch-checklist.pdf");
  const [emailSent, setEmailSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const { data } = await api.post("/leads/subscribe", {
        email: email.trim(),
        first_name: firstName.trim(),
        source: "home-hero",
      });
      setDownloadUrl(data.download_url || "/airbnb-launch-checklist.pdf");
      setEmailSent(!!data.email_sent);
      setStatus("done");
      toast.success(data.email_sent ? "Check your inbox — checklist on its way." : "You're in. Grab the checklist below.");
    } catch (err) {
      setStatus("idle");
      toast.error("Could not save your email. Try again in a moment.");
    }
  };

  if (status === "done") {
    return (
      <div
        data-testid="lead-capture-success"
        className="mt-8 max-w-xl rounded-2xl border border-brand-pink/40 bg-black/40 backdrop-blur-xl p-6"
      >
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-full bg-brand-pink/25 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 text-brand-pink" />
          </span>
          <div className="flex-1">
            <div className="font-display text-xl text-white">
              {emailSent ? "Check your inbox." : "You're on the list."}
            </div>
            <div className="text-sm text-white/70 mt-1">
              {emailSent
                ? "The Airbnb Launch Checklist is on its way from Kristy."
                : "Grab your checklist below — we'll follow up with more free playbooks."}
            </div>
            <Button
              asChild
              size="sm"
              className="mt-4 rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white"
              data-testid="lead-capture-download-btn"
            >
              <a href={downloadUrl} target="_blank" rel="noreferrer">
                <Download className="w-4 h-4 mr-2" /> Download the PDF
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="lead-capture-form"
      className="mt-8 max-w-xl rounded-2xl border border-white/15 bg-white/5 backdrop-blur-xl p-5"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-pink mb-3">
        <Sparkles className="w-3.5 h-3.5" /> Free gift · Airbnb Launch Checklist
      </div>
      <div className="text-white/80 text-sm mb-4 leading-relaxed">
        Drop your email — Kristy will send you her 34-step launch checklist (PDF).
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_auto] gap-2">
        <input
          type="text"
          placeholder="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          data-testid="lead-capture-first-name"
          className="rounded-full bg-white/10 border border-white/15 text-white placeholder-white/50 px-4 py-3 text-sm outline-none focus:border-brand-pink"
        />
        <input
          type="email"
          required
          placeholder="you@youremail.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="lead-capture-email"
          className="rounded-full bg-white/10 border border-white/15 text-white placeholder-white/50 px-4 py-3 text-sm outline-none focus:border-brand-pink"
        />
        <Button
          type="submit"
          disabled={status === "loading"}
          data-testid="lead-capture-submit"
          className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white px-6"
        >
          {status === "loading" ? "Sending…" : "Send it to me"}
        </Button>
      </div>
      <div className="text-[11px] text-white/40 mt-3">
        No spam. Unsubscribe in one click.
      </div>
    </form>
  );
}
